/**
 * Runtime detection helpers for Capacitor.
 *
 * The Capacitor global is injected by the native shell at start-up.
 * On plain Web / Android Chrome / Safari it is undefined, so all helpers
 * return safe defaults.
 *
 * ⚠️ Live-web mode: because the web app runs OFF the origin
 * (``https://myfleetapp.fr``) and NOT off a bundled ``public/`` folder,
 * the ``@capacitor/*`` npm packages are NOT loadable at runtime — a
 * dynamic ``import()`` would try to fetch ``https://myfleetapp.fr/@capacitor/browser``
 * and hit a 404. The reliable way to talk to a Capacitor plugin from
 * live-web JavaScript is via the global ``window.Capacitor.Plugins.<Name>``
 * that the native iOS shell auto-registers at start-up.
 *
 * These helpers are the single source of truth for that access — every
 * caller MUST go through ``getCapacitorPlugin`` and check for both the
 * plugin object AND the method it wants to call before invoking it.
 */
export function getCapacitor() {
  if (typeof window === "undefined") return null;
  return window.Capacitor || null;
}

export function isCapacitorNative() {
  const c = getCapacitor();
  if (!c) return false;
  try {
    return typeof c.isNativePlatform === "function" && c.isNativePlatform();
  } catch {
    return false;
  }
}

export function getCapacitorPlatform() {
  const c = getCapacitor();
  if (!c) return "web";
  try {
    return typeof c.getPlatform === "function" ? c.getPlatform() : "web";
  } catch {
    return "web";
  }
}

export function isCapacitorIOS() {
  return isCapacitorNative() && getCapacitorPlatform() === "ios";
}

export function isCapacitorAndroid() {
  return isCapacitorNative() && getCapacitorPlatform() === "android";
}

/**
 * Return the injected Capacitor plugin, or ``null`` when it is not
 * available (Web/Android environment, or the plugin was not installed
 * in the native iOS project).
 *
 * Usage:
 *   const Browser = getCapacitorPlugin('Browser');
 *   if (Browser?.open) await Browser.open({ url });
 */
export function getCapacitorPlugin(name) {
  const c = getCapacitor();
  if (!c) return null;
  const plugins = c.Plugins;
  if (!plugins || typeof plugins !== "object") return null;
  return plugins[name] || null;
}
